import * as React from "react";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import axios from "axios";

import { Loader } from "semantic-ui-react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { CUSTOMER_ROLES } from "@teamprevent/common/auth/roles";

import { toast } from "@teamprevent/common/toasts";

import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";

import { submitPpqClinicalNotes, savePpqClinicalNotes } from "../actions";
import { ApplicationState } from "../../applicationState";
import { AddTaskFormModel } from "../../task/model";

import { LineManagersApi } from "../../lineManagers/LineManagersApi";

import { HrRepresentativesApi } from "../../hrRepresentatives/HrRepresentativesApi";

import { loadPpqTriageTasks } from "../../triage/actions";

import { PpqClinicalNotesSections } from "./PpqClinicalNotesSections";

import { ClinicalNotes, questionnaireKey as clinicalNotesQuestionnaireKey } from "./ClinicalNotes";

const lineManagersApi = new LineManagersApi(axios);
const hrRepresentativesApi = new HrRepresentativesApi(axios);

export const outcomeQuestionId = "Outcome";

export const PpqClinicalNotes: React.FunctionComponent = () => {
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const customerId = useSelector((state: ApplicationState) => customerIdSelector(state));
    const featureToggles = getFeatureToggles();
    const enablePPQConsentLoop = featureToggles[FeatureToggleNames.EnablePPQConsentLoop];
    const preventPPQClinicalNotesDuplicateSubmission = featureToggles[FeatureToggleNames.PreventPPQClinicalNotesDuplicateSubmission];

    const dispatch = useDispatch();
    const [tasks, setTasks] = React.useState<AddTaskFormModel[]>([]);
    const [submitted, setSubmitted] = React.useState(false);
    const [managerOptions, setManagerOptions] = React.useState<{ id: string; name: string; email: string }[]>([]);
    const [hrUserOptions, setHrUserOptions] = React.useState<{ id: string; name: string; email: string }[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        dispatch(loadPpqTriageTasks());
    }, [dispatch]);

    React.useEffect(() => {
        async function fetchManagers() {
            setIsLoading(true);
            const [managers, hrUsers] = await Promise.all([
                lineManagersApi.getAll(customerId, CUSTOMER_ROLES.RECRUITMENT_MANAGER.name),
                hrRepresentativesApi.getAll(customerId)
            ]);

            setManagerOptions(managers.map(m => ({ id: m.id, name: m.name, email: m.email })));
            setHrUserOptions(hrUsers.map(m => ({ id: m.id, name: m.name, email: m.email })));
            setIsLoading(false);
        }

        if (enablePPQConsentLoop) {
            fetchManagers();
        }
    }, [customerId, enablePPQConsentLoop]);

    const submitQuestionnaire = React.useCallback((ohCaseId: string, taskId: string, _customerId: string, questions: AnsweredQuestionWithId[]) => {
        if (preventPPQClinicalNotesDuplicateSubmission && submitted) {
            toast.error("The form has already been submitted");
            return;
        }

        setSubmitted(true);
        dispatch(submitPpqClinicalNotes(ohCaseId, taskId, customerId, questions, clinicalNotesQuestionnaireKey, tasks));
    }, [customerId, dispatch, preventPPQClinicalNotesDuplicateSubmission, submitted, tasks]);

    const saveQuestionnaire = React.useCallback((ohCaseId: string, taskId: string, _customerId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        dispatch(savePpqClinicalNotes(ohCaseId, taskId, customerId, questions, eTag));
    }, [customerId, dispatch]);

    const tasksChanged = React.useCallback((updatedTasks: { task: AddTaskFormModel; isValid: boolean }[]) => {
        setTasks(updatedTasks.filter(updatedTask => updatedTask.isValid).map(t => t.task) );
    }, []);

    const intro = React.useMemo(() => {
        return `My name is ${currentUser.name} and I am an Occupational Health Advisor with Optima Health. Subsequent to receipt of your
        Pre-Placement Health Questionnaire, I would like to discuss with you some of the information you have provided so that we can provide your
        prospective employer with appropriate advice about your fitness for work and whether any adjustments to your role need to be considered.
        Is it convenient to talk to you now? If not, you can call back when it is convenient. Before we proceed I just need to check a few details
        to confirm I am talking to the right person.`;
    }, [currentUser]);

    if (isLoading)
    {
        return <Loader active />;
    }

    // When enablePPQConsentLoop feature toggle is removed, set showConsentSection flag to true
    return (
        <ClinicalNotes
            intro={intro}
            saveQuestionnaire={saveQuestionnaire}
            submitQuestionnaire={submitQuestionnaire}
        >
            <PpqClinicalNotesSections
                onTasksChange={tasksChanged}
                showErrors={submitted}
                hrRepresentatives={hrUserOptions}
                lineManagers={managerOptions}
                showConsentSection={enablePPQConsentLoop} />
        </ClinicalNotes>
    );
};
